import React, { useRef, useEffect, useContext } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { CLICK_PORTAL_SEARCH_FILTERS } from 'consts';
import { scrollTo } from 'utils/generalUtils';
import {
  SchoolFilters,
  MultiRowSchoolListingResults,
  ErrorPriority,
} from 'types';
import {
  handleSchoolResultsRequestWithUpdates,
  initImpressionDebugging,
} from 'app-requests/clickPortalSchoolManager';
import { getUserRegionCookie } from 'utils/analyticsHelpers';
import { LogError } from 'utils/logging/LogError';
import { LogInfo } from 'utils/logging';
import ClickPortalResultRowsList from './ClickPortalResultRowsList';
import { ClickPortalResultRowsSectionProps } from './wpToClickPortalResultRowsSectionTransform';

export default function ClickPortalResultRowsSection(
  props: ClickPortalResultRowsSectionProps
): React.JSX.Element {
  const { title, subTitle, resultRowsConfig, isCurated, mobileTitle } = props;
  const sectionRefs = useRef<(HTMLDivElement | null)[]>(
    resultRowsConfig?.map(() => null) || []
  );

  const {
    siteMeta: { schoolCode, siteType, clickPortalSettings },
    sessionInfo: { userSessionId },
    actions: { updateClickPortalData },
    clickPortal: { resultRows, isLoading },
    windowSize: { currentBreakpoint },
  } = useContext(GlobalContext);

  /**
   * This handles the request of school listing and mapping of its impression guids
   */
  async function handleSchoolResultsRequest(
    filters: SchoolFilters[],
    meta: { originalUrl: string } = { originalUrl: '' }
  ): Promise<MultiRowSchoolListingResults> {
    return handleSchoolResultsRequestWithUpdates(
      filters,
      siteType,
      userSessionId || '', // Pass userSessionId from context, fallback to empty string
      {
        isPersonalized: Boolean(getUserRegionCookie()),
        originalUrl: meta.originalUrl,
        schoolCode,
        isMultiRow: clickPortalSettings?.isMultiRow,
        isCurated,
        geoLocation: {},
        queryParams: {},
        cookies: {},
      },
      (data) => {
        scrollTo();
        updateClickPortalData(data);
      }
    );
  }

  useEffect(() => {
    LogInfo('ClickPortalResultRowsSection: Initializing impression debugging');
    initImpressionDebugging();
  }, []);

  useEffect(() => {
    /** Although userSessionId is not needed because we call for impression after results come back if needed
     * if we wait for sessionId this might improve impression tracking. It seems 15% of users are not having
     * impressions tracked. This might be due to them bouncing before the impression tracking is complete.
     */
    if (!userSessionId) {
      return;
    }

    try {
      const requestRows = resultRowsConfig.map((row) => ({
        [CLICK_PORTAL_SEARCH_FILTERS.FILTER_KEY]: row.filterKey || '',
        [CLICK_PORTAL_SEARCH_FILTERS.SUBJECTS]: {
          value: row.filters.parentCategory || undefined,
        },
        [CLICK_PORTAL_SEARCH_FILTERS.CONCENTRATIONS]: {
          value: row.filters.category || undefined,
        },
        [CLICK_PORTAL_SEARCH_FILTERS.DEGREES]: {
          value: row.filters.degreeType || undefined,
        },
      }));

      if (!requestRows.length) {
        return;
      }

      handleSchoolResultsRequest(requestRows, {
        originalUrl: window.location.pathname,
      });
    } catch (error) {
      LogError(error as Error, {
        priority: ErrorPriority.ONE,
        description: 'Error fetching click portal result rows from pageSection',
      });
    }
  }, [resultRowsConfig, userSessionId]);

  // Render loader components when loading, even if resultRows is empty

  const rowsToRender =
    isLoading && resultRows.length === 0
      ? resultRowsConfig.map((__, index) => ({
          hasImpressionGuids: false,
          title: resultRowsConfig[index]?.title || '',
          subTitle: resultRowsConfig[index]?.subTitle || '',
          schoolImpressionGroupGuid: '',
          results: [],
          hasTags: false,
          filterKeyDerivedValues: {
            hasDerivedValues: false,
            degree_level_guid: null,
            parent_category_guid: null,
            category_guid: null,
          },
        }))
      : resultRows;

  return (
    <section className="clickPortalResultRowsSection clickPortalResultRowsSection--isMultiRow">
      <div className="clickPortalResultRowsSection__inner">
        <div className="clickPortalResultRowsSection__header">
          <h2 className="clickPortalResultRowsSection__title">
            {currentBreakpoint === 'mobile' && mobileTitle
              ? mobileTitle
              : title}
          </h2>
          <span className="clickPortalResultRowsSection__subtitle">
            {subTitle}
          </span>
        </div>
        {rowsToRender.map((resultRow, index) => (
          <ClickPortalResultRowsList
            key={`${resultRow.title}-${resultRow.subTitle || index}-${
              resultRow.schoolImpressionGroupGuid
            }`}
            resultRow={resultRow}
            wpTitle={resultRowsConfig[index]?.title}
            wpSubTitle={resultRowsConfig[index]?.subTitle}
            scrollRef={{ current: sectionRefs.current[index] }}
            isLoading={isLoading}
            rowIndex={index}
          />
        ))}
      </div>
    </section>
  );
}
